import type { Component } from "vue"
import {
  Brain as BrainIcon,
  Search as SearchIcon,
  Loader2 as Loader2Icon,
  CheckCircle2 as CheckCircle2Icon,
  FolderOpen as FolderOpenIcon,
  RefreshCw as RefreshCwIcon,
  Download as DownloadIcon,
} from "lucide-vue-next"
import type { OrbState } from "@yogesharc/thinking-orbs/vanilla"
import type { BotTypingState, BotTypingUser, TypingUser } from "~/types/messages"

/** How long a typing/bot-status entry stays displayed after its last update if no "stopped"
 * event ever arrives — a safety margin over the other clients' own idle/cooldown timers. */
export const TYPING_DISPLAY_STALE_MS = 6000

// Each `BotTypingState` gets its own icon + verb rather than one generic "typing" indicator,
// since it reflects a distinct stage of the bot's work.
export const BOT_TYPING_DISPLAY: Record<BotTypingState, { icon: Component; label: (username: string) => string }> = {
  Thinking: { icon: BrainIcon, label: (u) => `${u} думает...` },
  Explored: { icon: SearchIcon, label: (u) => `${u} изучает...` },
  Processing: { icon: Loader2Icon, label: (u) => `${u} обрабатывает...` },
  Reviewed: { icon: CheckCircle2Icon, label: (u) => `${u} проверяет...` },
  Opened: { icon: FolderOpenIcon, label: (u) => `${u} открывает файл...` },
  ReSearch: { icon: RefreshCwIcon, label: (u) => `${u} повторяет поиск...` },
  Fetched: { icon: DownloadIcon, label: (u) => `${u} получает данные...` },
}

/** What the thinking orb shows for each stage of a bot's work (agent phantom message). */
export const BOT_TYPING_ORB: Record<BotTypingState, { state: OrbState; variant?: string }> = {
  Thinking: { state: "reasoning" },
  Explored: { state: "searching" },
  Processing: { state: "working" },
  Reviewed: { state: "reasoning", variant: "twins" },
  Opened: { state: "searching", variant: "lighthouse" },
  ReSearch: { state: "retrying" },
  Fetched: { state: "searching" },
}

export function liveTypingUsers(entries: Record<string, TypingUser> | undefined, now = Date.now()): TypingUser[] {
  if (!entries) return []
  return Object.values(entries).filter((u) => now - u.updatedAt < TYPING_DISPLAY_STALE_MS)
}

export function liveTypingBots(entries: Record<string, BotTypingUser> | undefined, now = Date.now()): BotTypingUser[] {
  if (!entries) return []
  return Object.values(entries).filter((b) => now - b.updatedAt < TYPING_DISPLAY_STALE_MS)
}

/** Russian plural form: `plural(3, "бот", "бота", "ботов")` -> "бота". */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/**
 * One short line summarising everything happening in a channel right now — humans typing and
 * bots working — for the chat header, which has a single truncated line of room. Names are
 * spelled out for up to `maxNames` typists (1 on a phone, 2 on desktop where the header pill has
 * more room); beyond that it collapses to a count ("3 печатают...") so the line stays readable at
 * any width rather than being cut mid-name. Returns "" when nothing is going on.
 */
export function compactActivityText(users: TypingUser[], bots: BotTypingUser[], maxNames = 1): string {
  const parts: string[] = []
  const names = users.map((u) => u.username || "Кто-то")
  if (names.length === 1) {
    parts.push(`${names[0]} печатает...`)
  } else if (names.length === 2 && maxNames >= 2) {
    parts.push(`${names[0]} и ${names[1]} печатают...`)
  } else if (names.length > 1) {
    parts.push(`${names.length} печатают...`)
  }
  if (bots.length === 1) {
    parts.push(BOT_TYPING_DISPLAY[bots[0].state].label(bots[0].username || "Бот"))
  } else if (bots.length > 1) {
    parts.push(`${bots.length} ${pluralRu(bots.length, "бот работает", "бота работают", "ботов работают")}...`)
  }
  return parts.join(" · ")
}
